import { useEffect, useState } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Button } from "@progress/kendo-react-buttons";
import { Loader } from "@progress/kendo-react-indicators";
import dayjs from 'dayjs';
import { fetchApi } from "../../services/api";
import { ApiLink } from "../../TypeGen/api-link";
import { plusIcon, trashIcon } from "@progress/kendo-svg-icons";
import { Checkbox } from "@progress/kendo-react-inputs";

const timeOptions = Array.from({ length: 49 }).map((_, i) => {
  const h = Math.floor(i / 2);
  const m = i % 2 === 1 ? 30 : 0;
  const value = `${h.toString().padStart(2,'0')}:${m === 0 ? '00' : '30'}`;
  return { Value: value, Name: dayjs({ hour: h, minute: m }).format('h:mm a') };
});

const locationTypes = [
  { Value: 'Office', Name: 'Office' },
  { Value: 'Remote', Name: 'Remote' }
];

type Schedule = {
  UserWorkScheduleID?: number;
  DayOfWeek: number;
  StartTime: string;
  EndTime: string;
  LocationType: string;
};

type ApiResponse = {
  UserName: string;
  JobTitle: string;
  LocalTimeZoneID: string;
  Schedules: Schedule[];
  Links: ApiLink[];
};

type Props = {
  userId: number;
  close: () => void;
  refresh: () => void;
};

const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const schedulePresets = [
  { label: "Day Shift (7AM - 3PM)", start: "07:00", end: "15:00" },
  { label: "Swing Shift (3PM - 11PM)", start: "15:00", end: "23:00" },
  { label: "Night Shift (11PM - 7AM)", start: "23:00", end: "07:00" }
];

const UserWorkScheduleDialog = ({ userId, close, refresh }: Props) => {
  const [data, setData] = useState<ApiResponse>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchApi(`/api/User/WorkSchedule/${userId}`)
      .then((resp: ApiResponse) => setData(resp));
  }, [userId]);

  const addSchedule = (day: number) => {
    setData(prev => ({
      ...prev,
      Schedules: [...prev.Schedules, { DayOfWeek: day, StartTime: '08:30', EndTime: '17:00', LocationType: 'Office' }]
    }));
  };

  const removeSchedule = (index: number) => {
    setData(prev => ({ ...prev, Schedules: prev.Schedules.filter((_, i) => i !== index) }));
  };

  const updateSchedule = (index: number, field: keyof Schedule, value: any) => {
    const schedules = [...data.Schedules];
    schedules[index] = { ...schedules[index], [field]: value };
    setData({ ...data, Schedules: schedules });
  };

  const toggleDay = (day: number) => {
    const exists = data.Schedules.some(s => s.DayOfWeek === day);
    if (exists) {
      setData(prev => ({ ...prev, Schedules: prev.Schedules.filter(s => s.DayOfWeek !== day) }));
    } else {
      addSchedule(day);
    }
  };

  const save = () => {
    setSaving(true);
    const link = data.Links.find(x => x.Name === 'Update');
    fetchApi(link.Link, { Schedules: data.Schedules }, link.Method)
      .then(() => { refresh(); close(); })
      .catch((e) => {
        setSaving(false);
        // If not problem details
        if (!e?.status) alert('Unable to save schedule.');
      });
  };

  const applyPreset = (presetIdx: number) => {
    // If no days selected, apply to Mon-Fri
    if (data.Schedules.length === 0) {
      setData(prev => ({
        ...prev,
        Schedules: days.slice(1, 6).map((_, i) => ({
          DayOfWeek: i + 1,
          StartTime: schedulePresets[presetIdx].start,
          EndTime: schedulePresets[presetIdx].end,
          LocationType: 'Office'
        }))
      }));
    }

    const preset = schedulePresets[presetIdx];
    setData(prev => ({
      ...prev,
      Schedules: days
        .map((_, i) => i)
        .filter(day => prev.Schedules.some(s => s.DayOfWeek === day))
        .flatMap(day => {
          // If shift goes over midnight
          if (preset.end < preset.start) {
            // Current day: start to 24:00
            // Next day: 00:00 to end
            const nextDay = (day + 1) % 7;
            return [
              {
                DayOfWeek: day,
                StartTime: preset.start,
                EndTime: "24:00",
                LocationType: "Office"
              },
              {
                DayOfWeek: nextDay,
                StartTime: "00:00",
                EndTime: preset.end,
                LocationType: "Office"
              }
            ];
          } else {
            // Normal shift
            return [{
              DayOfWeek: day,
              StartTime: preset.start,
              EndTime: preset.end,
              LocationType: "Office"
            }];
          }
        })
    }));
  };

  const dataView = () => {
    return <form className="k-form k-form-md p-0">
      <p>Working Timezone: {data.LocalTimeZoneID}</p>
      <div className="mb-2">
        {days.map((d,i) => <label key={i} className="mr-2">
          <Checkbox className="mr-1" label={d} checked={data.Schedules.some(s => s.DayOfWeek === i)} onChange={() => toggleDay(i)} />
        </label>)}
      </div>
      {days.map((d, dayIndex) => {
        const daySchedules = data.Schedules
          .map((s, idx) => ({ s, idx }))
          .filter(x => x.s.DayOfWeek === dayIndex);
        return (
          <div key={dayIndex} className="mb-2">
            {daySchedules.map(({ s, idx }, rowIdx) => (
              <div key={idx} className="d-flex align-items-center mb-1">
                <span style={{ width: 40 }}>{rowIdx === 0 ? d : ''}</span>
                <DropDownList
                  style={{ width: 110 }}
                  data={timeOptions}
                  textField="Name"
                  dataItemKey="Value"
                  value={timeOptions.find(x => x.Value === s.StartTime)}
                  onChange={e => updateSchedule(idx, "StartTime", e.value.Value)}
                />
                <span className="mx-1">to</span>
                <DropDownList
                  style={{ width: 110 }}
                  data={timeOptions}
                  textField="Name"
                  dataItemKey="Value"
                  value={timeOptions.find(x => x.Value === s.EndTime)}
                  onChange={e => updateSchedule(idx, "EndTime", e.value.Value)}
                />
                <DropDownList
                  style={{ width: 100 }}
                  className="ml-1"
                  data={locationTypes}
                  textField="Name"
                  dataItemKey="Value"
                  value={locationTypes.find(x => x.Value === s.LocationType)}
                  onChange={e => updateSchedule(idx, "LocationType", e.value.Value)}
                />
                {daySchedules.length == rowIdx + 1 && (
                  <Button svgIcon={plusIcon} themeColor="primary" fillMode="link" className="ml-1" onClick={() => addSchedule(dayIndex)} />
                )}
                {daySchedules.length == rowIdx + 1 && rowIdx > 0 && (
                  <Button svgIcon={trashIcon} themeColor="primary" fillMode="link" className="ml-1" onClick={() => removeSchedule(idx)} />
                )}
              </div>
            ))}
          </div>
        );
      })}
      <DropDownList
        style={{ width: 220, marginRight: 12 }}
        data={schedulePresets}
        textField="label"
        dataItemKey="label"
        onChange={e => applyPreset(schedulePresets.findIndex(p => p.label === e.value.label))}
        label="Quick schedule fill..."
      />
    </form>;
  };

  return <Dialog
      title={`${data?.UserName}`}
      className="dialog-w525"
      onClose={close}
    >
      {data === null ? <div className="k-pos-absolute k-top-center mt-5"><Loader type="converging-spinner" /></div> : dataView()}
      <DialogActionsBar>
        <Button disabled={saving} onClick={close}>Cancel</Button>
        <Button disabled={saving} themeColor="primary" onClick={save}>Save</Button>
      </DialogActionsBar>
    </Dialog>;
};

export default UserWorkScheduleDialog;